import { useState } from 'react';
import { Badge, Group, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { TenantColumn } from './FlagMatrixGrid';
import { DistributionBar } from './FlagMatrixShared';
import { STATE_META, StateCounts, resolvedOnCount, targetedCount } from './flagMatrixTypes';

type SortKey = 'overrides' | 'on' | 'name';

type Props = {
  tenants: TenantColumn[];
  countsFor: (tenantName: string) => StateCounts;
  flagTotal: number;
  onSelectTenant: (tenantName: string) => void;
};

const SORT_OPTIONS = [
  { value: 'overrides', label: 'Most overrides' },
  { value: 'on', label: 'Most flags on' },
  { value: 'name', label: 'Name' },
];

export default function TenantCentricList({
  tenants,
  countsFor,
  flagTotal,
  onSelectTenant,
}: Props) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('overrides');

  const query = search.trim().toLowerCase();
  const rows = tenants
    .filter(tenant => !query || tenant.tenantName.toLowerCase().includes(query))
    .map(tenant => ({ tenant, counts: countsFor(tenant.tenantName) }))
    .sort((a, b) => {
      if (sort === 'on') return resolvedOnCount(b.counts) - resolvedOnCount(a.counts);
      if (sort === 'name') return a.tenant.tenantName.localeCompare(b.tenant.tenantName);
      return targetedCount(b.counts) - targetedCount(a.counts);
    });

  return (
    <Stack gap="sm">
      <Group>
        <TextInput
          placeholder="Search tenants"
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
        />
        <Select
          data={SORT_OPTIONS}
          value={sort}
          onChange={value => setSort((value as SortKey) ?? 'overrides')}
          allowDeselect={false}
        />
      </Group>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tenant</Table.Th>
            <Table.Th w={220}>Distribution across flags</Table.Th>
            <Table.Th w={100}>On</Table.Th>
            <Table.Th w={90}>Overrides</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(({ tenant, counts }) => (
            <Table.Tr
              key={tenant.tenantName}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectTenant(tenant.tenantName)}
            >
              <Table.Td>
                <Group gap={8} wrap="nowrap">
                  <Text ff="monospace" size="sm">
                    {tenant.tenantName}
                  </Text>
                  {tenant.internal && (
                    <Badge size="xs" variant="light" color="blue">
                      Internal
                    </Badge>
                  )}
                  {tenant.error && (
                    <Badge size="xs" variant="light" color={STATE_META.failed.colour.split('.')[0]}>
                      {tenant.error}
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              {tenant.error ? (
                <Table.Td colSpan={3}>
                  <Text size="sm" c="dimmed">
                    Not evaluated.
                  </Text>
                </Table.Td>
              ) : (
                <>
                  <Table.Td>
                    <DistributionBar counts={counts} total={flagTotal} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {resolvedOnCount(counts)}{' '}
                      <Text span c="dimmed">
                        / {flagTotal}
                      </Text>
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={targetedCount(counts) ? 'blue.7' : 'dimmed'}>
                      {targetedCount(counts)}
                    </Text>
                  </Table.Td>
                </>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
